/**
 {
    "time": "2022-03-25T00:00:00Z",
    "date": "2022-03-25",
    "close": "1.0982",
    "high": "1.1038",
    "low": "1.09811",
    "open": "1.09995"
  },
 */

import { openTimeByGroup } from '@/store/config';
import { CONSTANT } from '@/store/constants';
import { usePairs } from '@/store/pairs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Bar } from 'public/charting_library/charting_library';

dayjs.extend(utc);
dayjs.extend(timezone);

export function isMarketOpen(
    group: keyof typeof openTimeByGroup,
    ts: number
): boolean {
    const cfg = openTimeByGroup[group];
    if (!cfg) return true; // nếu không có config thì coi như luôn mở

    // convert timestamp sang timezone đã định nghĩa (ở đây là America/New_York)
    const d = dayjs.tz(ts, cfg.timeZone);

    // 1. Holiday check
    const dayMonth = d.format('DD-MM');
    if (cfg.holidays?.includes(dayMonth)) return false;

    // 2. Early close check
    const earlyClose = cfg.earlyCloses?.find((e) => e.date === dayMonth);
    if (earlyClose) {
        const closeTime = dayjs.tz(
            `${d.format('YYYY-MM-DD')} ${earlyClose.close}`,
            cfg.timeZone
        );
        if (d.isAfter(closeTime)) return false;
    }

    // 3. Weekly schedule
    const weekday = d.day(); // 0 = Sunday, 6 = Saturday
    const schedule = cfg.openTime[weekday];
    if (!schedule || schedule.start === null || schedule.end === null)
        return false;

    const start = schedule.start
        ? dayjs.tz(`${d.format('YYYY-MM-DD')} ${schedule.start}`, cfg.timeZone)
        : null;
    const end = schedule.end
        ? dayjs.tz(`${d.format('YYYY-MM-DD')} ${schedule.end}`, cfg.timeZone)
        : null;

    if (start && d.isBefore(start)) return false;
    if (end && d.isAfter(end)) return false;

    return true;
}

export interface ForexHistoricalData {
    time: string;
    date: string;
    close: string | undefined | null;
    high: string | undefined | null;
    low: string | undefined | null;
    open: string | undefined | null;
}

export async function getHistoricalDataHoldStation(opts: {
    symbol: string;
    interval: string;
    limit?: number;
    from?: number;
    to?: number;
}) {
    const queryParams: Record<string, any> = {
        timeframe: opts.interval,
        to: opts?.to,
    };
    const pair = usePairs.getState().data;

    const groupName = pair.find(
        (item) => item.symbol === opts.symbol
    )?.groupName;

    const params = new URLSearchParams(queryParams);
    const url = `${CONSTANT.URL_HISTORY_HOLDSTATION}/${
        opts.symbol
    }?${params.toString()}`;
    console.debug('[FOREX]', 'request historical data', url);

    const data = await fetch(url);
    if (data.status < 200 || data.status >= 400) {
        throw new Error(`${data.status}`);
    }

    const resp = await data.json();

    if (!resp || !Array.isArray(resp)) {
        throw new Error('Historical data empty');
    }

    // 🔹 Sắp xếp theo time ASC
    let result: ForexHistoricalData[] = resp.sort(
        (a, b) => Number(a.time) - Number(b.time)
    );

    if (groupName === 'stock' && opts.interval !== '1d') {
        result = result.filter((o) => {
            const ts = Number(o.time) * 1000;
            return isMarketOpen('stock', ts); // dùng "stock" / "forex" / "commodities-1"
        });
    }

    const returnData = result.map((o, i, arr) => {
        const prevClose =
            i > 0
                ? parseFloat(arr[i - 1].close || '0')
                : parseFloat(o.open || '0');

        const ele: Bar = {
            time: dayjs
                .tz(Number(o.time) * 1000, 'UTC')
                .toDate()
                .getTime(),
            open: prevClose,
            high: parseFloat(o.high || '0'),
            low: parseFloat(o.low || '0'),
            close: parseFloat(o.close || '0'),
        };

        return ele;
    });

    return returnData;
}
